import { Dispatch, FormEvent, SetStateAction, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  WppActionButton,
  WppButton,
  WppIconAdd,
  WppIconAddCircle,
  WppInput,
  WppTextareaInput,
  WppTypography,
} from '@wppopen/components-library-react'
import { scrollToError } from './utils'
import { InputChangeEventDetail, TextareaInputChangeEventDetail } from '@wppopen/components-library'
import appContext from 'context/appContext'
import type {
  WppInputCustomEvent,
  WppTextareaInputCustomEvent,
} from '@wppopen/components-library/dist/types/components'
import { GenericModal } from 'components/Modals'
import { NoneAddedRender } from 'components/FormComponents/NoneAddedRender/NoneAddedRender'
import { ERROR_MESSAGES } from 'utils/constants'
import brandFormReducer, {
  BrandFormReducerState,
  UpdateBrandListField,
  UpdateBrandSingleInputField,
  defaultBrandErrorObject,
  emptyBrandFormReducerState,
  formHelperFunctions,
  initialBrandFormReducerState,
} from 'reducers/CreateAndEditBrandReducer/CreateAndEditBrandReducer'
import { getType, transformStringToId } from 'utils'
import useCurrentUser from 'hooks/useCurrentUser'
import KeywordInput from 'components/FormComponents/KeywordInput/KeywordInput'
import FormList from 'components/FormComponents/FormList/FormList'
import BrandLanguageBlock from 'components/FormComponents/BrandLanguageBlock/BrandLanguageBlock'
import { BlockTypes } from 'reducers/CreateAndEditRetailerReducer/utils'
import clsx from 'clsx'
import { FEApiError } from 'utils/errorHandling'
import {
  type BrandLanguageFormReducerState,
  brandLanguageFormHelperFunctions,
} from 'reducers/CreateAndEditRetailerReducer/RetailerBrandLanguageReducer'
import FormAccordionSection from 'components/FormAccordionSection/FormAccordionSection'
import type { StateToMultiInputFieldMap } from 'reducers/FormReducer/FormReducer'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils.d'
import type { BrandsCreatePayload, BrandsDetailData } from '@contracts'
import styles from './CreateAndEditNewBrandForm.module.scss'

interface CreateAndEditNewBrandFormProps {
  // used to pre-populate the form if the form is being edited
  initialData?: BrandsDetailData
  isApiLoading?: boolean
  isLoading?: boolean
  onSubmitForm: (data: BrandsCreatePayload) => Promise<unknown>
  isFormActive?: boolean
  isTitleRequired?: boolean
  redirectToNewBrandView?: boolean
  client_id: string
  setToastDetails?: Dispatch<SetStateAction<ToastDetails | undefined>>
}

export default function CreateAndEditNewBrandForm({
  initialData,
  onSubmitForm,
  isLoading,
  isFormActive,
  redirectToNewBrandView,
  client_id,
  setToastDetails,
}: CreateAndEditNewBrandFormProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { userName } = useCurrentUser()
  const { fetchClientData, configApi } = useContext(appContext)
  const { data: configData } = configApi
  const initialFormReducerState = useMemo<BrandFormReducerState>(() => {
    if (initialData) return initialBrandFormReducerState(initialData)
    return emptyBrandFormReducerState
  }, [initialData])
  const formReducer = useReducer(brandFormReducer, initialFormReducerState)
  const [formReducerState, formReducerDispatch] = formReducer

  const {
    handleError,
    updateSingleInput: updateInput,
    addNewFormBlock,
    resetForm,
    setBrandLanguage,
  } = formHelperFunctions
  const [isCreateAndEditModalOpen, setIsCreateAndEditModalOpen] = useState(false)
  const [hasScrolledToError, setHasScrolledToError] = useState<Partial<Record<keyof BrandFormReducerState, boolean>>>({
    brand_name: false,
    brand_language: false,
    tone_of_voice: false,
  })

  const TOV_CHARACTER_LIMIT = 1000

  const handleInput = (
    e: WppInputCustomEvent<InputChangeEventDetail> | WppTextareaInputCustomEvent<TextareaInputChangeEventDetail>,
  ) => {
    const { value, name } = e.target

    // ensure that the name is a valid input field according to what is defined in the reducer
    const typeSafeInputFieldKey = name as UpdateBrandSingleInputField

    formReducerDispatch(updateInput(typeSafeInputFieldKey, value))

    // if creating a new brand, set the id to the processed brand name
    if (typeSafeInputFieldKey === 'brand_name' && !initialData)
      formReducerDispatch(updateInput('id', transformStringToId(value)))

    // handle mandatory field error message
    if (formReducerState[typeSafeInputFieldKey].errorListeners?.includes('requiredField')) {
      const validationObject = defaultBrandErrorObject.requiredField?.find(({ id }) => id === typeSafeInputFieldKey)
      if (validationObject) {
        // if the field is not empty and there is an error message, remove the error message
        if (!formReducerState[typeSafeInputFieldKey].error && !!value.trim()) {
          validationObject.passesValidation = true
        } else {
          validationObject.passesValidation = false
          formReducerDispatch(
            handleError(typeSafeInputFieldKey, !!value.trim() ? undefined : ERROR_MESSAGES.MANDATORY_FIELD),
          )
        }
      }
    }

    if (typeSafeInputFieldKey === 'tone_of_voice') {
      const validationCondition = value.trim().length <= TOV_CHARACTER_LIMIT
      // if the field is not empty and there is an error message, remove the error message
      if (formReducerState[typeSafeInputFieldKey].error && validationCondition) {
        formReducerDispatch(
          handleError(typeSafeInputFieldKey, validationCondition ? undefined : 'Character limit exceeded'),
        )
      }
    }
  }

  // submit form
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    let hasError = false

    // validate form brand name
    if (!formReducerState.brand_name.value.trim()) {
      formReducerDispatch(handleError('brand_name', 'Brand name is required'))
      hasError = true
    }

    // validate the character limit of style guide tone of voice
    const styleGuideToneOfVoice = formReducerState.tone_of_voice
    if (
      styleGuideToneOfVoice &&
      styleGuideToneOfVoice.value &&
      styleGuideToneOfVoice.value.length > TOV_CHARACTER_LIMIT
    ) {
      formReducerDispatch(handleError('tone_of_voice', 'Character limit exceeded'))
      hasError = true
    }

    // validate form brand language if exist brand language
    formReducerState.brand_language.forEach(({ region, replacement, patterns }, idx) => {
      const { error: regionError, value: regionValue } = region
      const { error: replacementError, value: replacementValue } = replacement
      const { error: patternsError, value: patternsValue } = patterns

      if (regionError || replacementError || patternsError) {
        hasError = true
        return
      }

      const setBrandLanguageError = (field: keyof BrandLanguageFormReducerState, message: string) => {
        formReducerDispatch(setBrandLanguage(brandLanguageFormHelperFunctions.handleError(field, message), idx))
        hasError = true
      }

      if (!regionValue.trim()) setBrandLanguageError('region', 'Please select a Region')

      if (!replacementValue.trim()) setBrandLanguageError('replacement', 'Brand term field is required')

      if (!patternsValue.length) setBrandLanguageError('patterns', 'At least one entry is required')
    })

    // has error return
    if (
      !!formReducerState.brand_name.error ||
      hasError ||
      (initialData && JSON.stringify(initialFormReducerState) === JSON.stringify(formReducerState))
    ) {
      const errorElement = document.querySelectorAll('[message-type="error"]')
      scrollToError(errorElement[0])
      return
    }
    // open modal to type username
    setIsCreateAndEditModalOpen(true)
  }

  // submit modal
  const handleModalSubmit = async (comment: string | undefined) => {
    // generate Brand API data
    const brandDataApiData: BrandsCreatePayload = {
      client_id,
      id: formReducerState.id.value || transformStringToId(formReducerState.brand_name.value),
      brand_name: formReducerState.brand_name.value,
      config: {
        content_config: {
          // guideline should be filtered out to remove all empty strings
          guidelines: formReducerState.guidelines.value.filter(value => !!value.trim()) || [],
          guidelines_shortener: formReducerState.guidelines_shortener.value || [],
          restrictions: formReducerState.restrictions.value || [],
          style_guide: {
            target_audience: formReducerState.target_audience.value,
            tone_of_voice: formReducerState.tone_of_voice.value,
          },
        },
      },
      brand_keywords: {
        priority: formReducerState.keyword_priority.value,
        required: formReducerState.keyword_required.value,
        optional: formReducerState.keyword_optional.value,
        omit: formReducerState.keyword_omit.value,
        keyphrases_required: formReducerState.keyphrase_required.value,
        keyphrases_optional: formReducerState.keyphrase_optional.value,
      },

      brand_language: formReducerState.brand_language.map(({ region, replacement, patterns }) => ({
        region: region.value,
        replacement: replacement.value,
        patterns: patterns.value,
      })),
      update_info: {
        from_version: initialData?.version || 1,
        user_name: userName,
        comment: comment,
      },
    }

    if (initialData) delete brandDataApiData.id

    try {
      const response = await onSubmitForm(brandDataApiData)
      if (response instanceof Object && Reflect.has(response, 'errors')) throw new Error('something went wrong')

      await fetchClientData()
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('version')
      setSearchParams(newSearchParams)

      if (redirectToNewBrandView)
        navigate(`/settings/client/${client_id}/brand/${formReducerState.id.value || initialData?.id}`)
    } catch (err) {
      const handleBannerError = () => {
        if (setToastDetails)
          setToastDetails({
            message: 'Something went wrong',
            type: 'error',
          })
      }
      const error = err as FEApiError<BrandsCreatePayload>
      const { isValidationError } = error
      if (!isValidationError) return handleBannerError()

      Object.values(error.errors!).forEach(({ path: fullPath, message }) => {
        const bannerOverrideFields: (keyof BrandsCreatePayload)[] = ['client_id']
        let path: keyof BrandsCreatePayload
        // a correction map to display an error in a different field if the returned path is not visible in the UI
        error.fieldOverrideMap = new Map<keyof BrandsCreatePayload, keyof BrandsCreatePayload>([
          ['id', 'brand_name'],
          ['_id', 'brand_name'],
        ])

        const splitPath = fullPath.split('.')
        const isBrandLanguageError = splitPath[0] === getType<keyof BrandFormReducerState>('brand_language')
        path = error.fieldOverrideMap.has(fullPath as keyof BrandsCreatePayload)
          ? error.fieldOverrideMap.get(fullPath as keyof BrandsCreatePayload)!
          : (splitPath.pop() as keyof BrandsCreatePayload)

        if (bannerOverrideFields.includes(path) || !Reflect.has(formReducerState, path)) {
          return handleBannerError()
        }

        if (isBrandLanguageError) {
          const idx = Number(splitPath[1])
          // display only the field name that is returned in the error handling
          // e.g. brand_language[0].region => region
          const userFriendlyMessage = path ? message.replace(/brand_language\[[0-9]+\]\./g, '') : message
          formReducerDispatch(
            setBrandLanguage(
              brandLanguageFormHelperFunctions.handleError(
                path as keyof BrandLanguageFormReducerState,
                userFriendlyMessage,
              ),
              idx,
            ),
          )
        } else {
          // display only the field name that is returned in the error handling
          // e.g. config.content_config.style_guide.tone_of_voice => tone_of_voice
          const userFriendlyMessage = path ? message.replace(fullPath, path) : message
          formReducerDispatch(handleError(path as keyof BrandFormReducerState, userFriendlyMessage))
        }
      })
    } finally {
      setIsCreateAndEditModalOpen(false)
    }
  }

  // reset form if form is not active
  useEffect(() => {
    if (!isFormActive && initialData) formReducerDispatch(resetForm(initialFormReducerState))
  }, [isFormActive])

  // scroll to error field
  useEffect(() => {
    const errorElement = document.querySelectorAll('[message-type="error"]')
    // if no error element return
    if (!errorElement.length) return
    const fieldName = errorElement[0].getAttribute('name') as keyof BrandFormReducerState
    if (hasScrolledToError[fieldName]) return
    setHasScrolledToError({ ...hasScrolledToError, [fieldName]: true })
    // if element is in screen view return
    if (errorElement[0].getBoundingClientRect().top > 0) return
    scrollToError(errorElement[0])
  }, [formReducerState.brand_name.error, formReducerState.brand_language, formReducerState.tone_of_voice.error])

  return (
    <div className={styles.createAndEditForm} data-testid="form-wrapper">
      <form id="brand-form" onSubmit={e => handleSubmit(e)}>
        {/* brand details */}
        <div className={styles.midContainer}>
          <WppInput
            placeholder="Enter brand name e.g. XAIA"
            labelConfig={{
              text: 'Brand name',
            }}
            required
            value={formReducerState.brand_name.value}
            messageType={!!formReducerState.brand_name.error ? 'error' : undefined}
            message={formReducerState.brand_name.error}
            name="brand_name"
            data-testid="brand-name-input"
            disabled={!isFormActive}
            onWppChange={handleInput}
          />

          <WppInput
            placeholder={isFormActive ? 'Please enter' : '-'}
            labelConfig={{
              text: 'Target Audience',
              icon: 'wpp-icon-info',
              description:
                'Describe the target audience for the content, this could include details like age, gender, interests and profession.',
            }}
            disabled={!isFormActive}
            name={getType<UpdateBrandSingleInputField>('target_audience')}
            value={formReducerState.target_audience.value}
            onWppChange={handleInput}
            messageType={!formReducerState.target_audience.error ? undefined : 'error'}
            message={formReducerState.target_audience.error || ''}
            data-testid="style-guide-input"
          />
        </div>

        <div className={styles.fields_group}>
          <WppTextareaInput
            labelConfig={{
              text: 'Tone of voice',
              icon: 'wpp-icon-info',
              description:
                "Define the tone and style of the brand's messaging. Should it be professional, playful, casual or technical? This helps ensure AI generated content aligns with the brand's identity.",
            }}
            name={getType<UpdateBrandSingleInputField>('tone_of_voice')}
            onWppChange={handleInput}
            value={formReducerState.tone_of_voice.value}
            placeholder={isFormActive ? 'Please enter' : '-'}
            rows={10}
            charactersLimit={TOV_CHARACTER_LIMIT}
            disabled={!isFormActive}
            maxMessageLength={TOV_CHARACTER_LIMIT}
            warningThreshold={isFormActive ? TOV_CHARACTER_LIMIT - 100 : undefined}
            messageType={!!formReducerState.tone_of_voice.error ? 'error' : undefined}
            message={formReducerState.tone_of_voice.error || ''}
            className={styles.large_text_area}
            data-testid="style-guide-input"
          />
        </div>

        {/* AI guidelines */}
        <div className={styles.fields_group}>
          <FormList<'BrandFormReducerState'>
            disabled={!isFormActive}
            id={getType<UpdateBrandListField>('guidelines')}
            label="Guidelines"
            state={formReducerState}
            reducerDispatch={formReducerDispatch}
            tooltip="Add guidelines that should be followed, such as formatting rules, prohibited words or compliance requirements."
          />
        </div>

        {/* brand language */}
        <div className={styles.fields_group}>
          <FormAccordionSection
            tooltip='Brand language is used to replace brand terms in the generated content. For example, if you want to replace "XAIA" with "XAIA AI", you can add a brand language entry with the region "all" and the replacement "XAIA AI".'
            text="Brand language (optional)"
            fullWidthChildren={
              <>
                <div className={styles.fields_group__addContainer}>
                  {!isFormActive && !formReducerState.brand_language.length && <NoneAddedRender />}
                  {!!formReducerState.brand_language.length && (
                    <>
                      {formReducerState.brand_language.map((brand, languageIdx) => (
                        <BrandLanguageBlock
                          key={languageIdx}
                          configData={configData}
                          disabled={!isFormActive}
                          formReducer={formReducer}
                          idx={languageIdx}
                        />
                      ))}
                    </>
                  )}
                  {isFormActive &&
                    (!formReducerState.brand_language.length ? (
                      <WppButton
                        variant="secondary"
                        data-testid="add-brand-language-cta"
                        onClick={() => formReducerDispatch(addNewFormBlock(BlockTypes.BRAND_LANGUAGE))}
                      >
                        <WppIconAddCircle slot="icon-start" />
                        Add
                      </WppButton>
                    ) : (
                      <WppActionButton
                        disabled={!isFormActive}
                        onClick={() => formReducerDispatch(addNewFormBlock(BlockTypes.BRAND_LANGUAGE))}
                        data-testid="add-another-brand-language-cta"
                      >
                        <WppIconAdd slot="icon-start" />
                        Add Another
                      </WppActionButton>
                    ))}
                </div>
                {!isFormActive && !formReducerState.brand_language.length && <NoneAddedRender />}
              </>
            }
          />
        </div>

        {/* keywords */}
        <FormAccordionSection
          isFormActive={isFormActive}
          text="Keywords (optional)"
          fullWidthChildren={
            <>
              <div className={clsx(styles.twoColumnContainer)}>
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyword_priority')}
                  label="Priority"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword
                  tooltip="Keywords that should be prioritised in the content to ensure they are prominently featured e.g in the title."
                />
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyword_required')}
                  label="Required"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword
                  tooltip="Specify keywords that must be included in the content without exception."
                />
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyword_optional')}
                  label="Optional"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword
                  tooltip="List keywords that can be included if relevant but are not mandatory."
                />
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyword_omit')}
                  label="Omit"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword
                  tooltip="Identify keywords that should be excluded from the content entirely."
                />
              </div>

              <div className={styles.inputGap}>
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyphrase_required')}
                  label="Required Keyphrases"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword={false}
                  tooltip="Enter keyphrases that must appear in the content exactly as specified."
                />
                <KeywordInput
                  id={getType<StateToMultiInputFieldMap<'BrandFormReducerState'>>('keyphrase_optional')}
                  label="Optional Keyphrases"
                  disabled={!isFormActive}
                  formReducer={formReducer}
                  isKeyword={false}
                  tooltip="List keyphrases that may be included in the content if appropriate, but are not required."
                />
              </div>
            </>
          }
        />
      </form>

      {/* create and edit modal to save */}
      {isCreateAndEditModalOpen && (
        <GenericModal
          shouldModalOpen={isCreateAndEditModalOpen}
          modalTitle="Save changes"
          onSubmit={({ comment }) => handleModalSubmit(comment)}
          onCancel={() => setIsCreateAndEditModalOpen(false)}
          submitButtonText="Save"
          isApiLoading={isLoading || false}
          fieldsConfig={{
            hasCommentField: true,
            isCommentRequiredField: false,
          }}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            This action will create a new version.
            <br />
            After you save version this action cannot be undone.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}
